import { ProductComponent } from '@theme/view-event-elements';
import { CartLinesUpdateEvent } from '@shopify/events';

class VoceProductCard extends ProductComponent {
  connectedCallback() {
    super.connectedCallback();
    this._abort?.abort();
    this._abort = new AbortController();
    const options = { signal: this._abort.signal };
    this.addEventListener('click', this.handleCardClick, options);
    this.addEventListener('pointerover', this.preloadHoveredColor, options);
    this.addEventListener('focusin', this.preloadHoveredColor, options);
    if ('IntersectionObserver' in window) {
      this._colorObserver?.disconnect();
      this._colorObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.querySelectorAll('[data-vc-color]').forEach(button => this.preloadColor(button));
        this._colorObserver?.disconnect();
      }, { rootMargin: '100px' });
      this._colorObserver.observe(this);
    }
    this.querySelector('.vc-size-sheet')?.addEventListener('close', this.restorePanel, options);
  }

  disconnectedCallback() {
    this.restorePanel();
    this._abort?.abort();
    this._colorObserver?.disconnect();
    super.disconnectedCallback();
  }

  restorePanel = () => {
    const panel = this.querySelector('.vc-size-sheet .vc-card__panel');
    if (panel) this.appendChild(panel);
  };

  handleCardClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const colorButton = event.target.closest('[data-vc-color]');
    if (colorButton) { event.preventDefault(); event.stopPropagation(); this.selectColor(colorButton); return; }
    const openButton = event.target.closest('[data-vc-open]');
    const closeButton = event.target.closest('[data-vc-close]');
    const sizeButton = event.target.closest('[data-vc-variant]');
    const sheet = this.querySelector('.vc-size-sheet');
    const preorderClose = event.target.closest('[data-vc-preorder-close]');
    if (preorderClose) { event.preventDefault(); this.querySelector('.vc-preorder-dialog')?.close(); return; }
    if (openButton && !openButton.disabled) {
      event.preventDefault();
      const panel = this.querySelector('.vc-card__panel');
      if (sheet && panel && !sheet.open) {
        sheet.appendChild(panel);
        sheet.showModal();
      }
    } else if (closeButton || event.target === sheet) {
      event.preventDefault();
      sheet?.close();
    } else if (sizeButton && !sizeButton.disabled) {
      event.preventDefault();
      event.stopPropagation();
      if (sizeButton.dataset.vcSelectedUrl) { window.location.assign(sizeButton.dataset.vcSelectedUrl + '?variant=' + encodeURIComponent(sizeButton.dataset.vcVariant)); return; }
      if (this.dataset.vcPreorder === 'true') this.openPreorder(sizeButton);
      else this.addSizeToCart(sizeButton);
    }
  };

  preloadColor(button) {
    const url = button?.dataset.productImage;
    if (!url) return;
    window.__voceColorImageCache ||= new Map();
    if (window.__voceColorImageCache.has(url)) return;
    const image = new Image();
    image.src = url;
    window.__voceColorImageCache.set(url, image);
  }

  preloadHoveredColor = (event) => {
    if (!(event.target instanceof Element)) return;
    this.preloadColor(event.target.closest('[data-vc-color]'));
  };

  selectColor(button) {
    const image = this.querySelector('.vc-card__image');
    if (image && button.dataset.productImage) {
      image.srcset = '';
      image.src = button.dataset.productImage;
    }
    const hoverImage = this.querySelector('.vc-card__hover-image');
    if (hoverImage) {
      const hoverUrl = button.dataset.productHoverImage;
      if (hoverUrl) {
        hoverImage.srcset = '';
        hoverImage.src = hoverUrl;
        this.dataset.vcHasHover = 'true';
      } else {
        delete this.dataset.vcHasHover;
      }
    }
    this.querySelectorAll('[data-vc-color]').forEach(el => el.setAttribute('aria-pressed', String(el === button)));
    this.querySelectorAll('.vc-card__link, .vc-card__title-link').forEach(el => { el.href = button.dataset.productUrl; });
    const preorderURL = this.querySelector('[name="contact[Product URL]"]');
    if (preorderURL) preorderURL.value = new URL(button.dataset.productUrl, window.location.origin).href;
    const variants = JSON.parse(button.dataset.variants || '[]');
    const isPreorder = button.dataset.preorder === 'true';
    const allOutOfStock = variants.length > 0 && variants.every(v => !v.in_stock);
    const stockBadge = this.querySelector('[data-vc-stock-badge]');
    const discountBadge = this.querySelector('[data-vc-discount-badge]');
    const comingBadge = this.querySelector('.vc-card__coming-soon');
    if (stockBadge) stockBadge.hidden = !allOutOfStock;
    if (discountBadge) discountBadge.hidden = allOutOfStock;
    if (comingBadge) comingBadge.hidden = allOutOfStock;
    const canUseSameFlow = isPreorder === (this.dataset.vcPreorder === 'true');
    this.querySelectorAll('[data-vc-variant]').forEach(sizeButton => {
      const selectedSize = (sizeButton.dataset.vcSize || '').trim().toLowerCase();
      const matches = variants.filter(v => v.options.some(value => String(value).trim().toLowerCase() === selectedSize));
      const variant = matches.find(v => v.available) || matches[0];
      const outOfStock = matches.length > 0 && matches.every(v => !v.in_stock);
      sizeButton.textContent = sizeButton.dataset.vcSize;
      sizeButton.classList.toggle('vc-size-out-of-stock', outOfStock);
      sizeButton.setAttribute('aria-label', sizeButton.dataset.vcSize + (outOfStock ? ' — Out of stock' : ''));
      if (variant) {
        sizeButton.dataset.vcVariant = variant.id;
        sizeButton.dataset.vcPrice = variant.price;
        sizeButton.dataset.vcSku = variant.sku || "";
        sizeButton.disabled = !variant.available && !isPreorder;
      } else sizeButton.disabled = true;
      sizeButton.dataset.vcSelectedUrl = canUseSameFlow ? '' : button.dataset.productUrl;
    });
    this.dataset.productId = button.dataset.productId;
  }

  openPreorder(button) {
    const dialog = this.querySelector('.vc-preorder-dialog');
    if (!dialog) return;
    const size = button.dataset.vcSize || button.textContent.trim();
    const price = button.dataset.vcPrice || '';
    dialog.querySelector('[data-vc-preorder-size]').value = size;
    dialog.querySelector('[data-vc-preorder-price-field]').value = price;
    dialog.querySelector('[data-vc-preorder-price]').textContent = price;
    const name = this.querySelector('[name="contact[Product]"]').value;
    dialog.querySelector('[data-vc-preorder-body]').value = 'Pre-order request for ' + name + ' | Size: ' + size + (this.dataset.vcHasLength === 'false' ? '' : ' | Length: 30 (Standard)') + ' | Total price: ' + price;
    const productURL = dialog.querySelector('[name="contact[Product URL]"]').value;
    const details = name + ' | Product URL: ' + productURL + ' | SKU: ' + (button.dataset.vcSku || 'Not available') + ' | Size: ' + size + (this.dataset.vcHasLength === 'false' ? '' : ' | Length: 30 (Standard)') + ' | Price: ' + price;
    dialog.querySelector('[data-vc-preorder-formester]').src = 'https://dgpkvzkq.formester.com/f/9pkFdncP9?' + new URLSearchParams({ 'Single Line Input': details }).toString();
    const sheet = this.querySelector('.vc-size-sheet');
    if (sheet?.open) sheet.close();
    this.restorePanel();
    dialog.showModal();
  }

  async addSizeToCart(button) {
    const panel = button.closest('.vc-card__panel');
    const status = panel?.querySelector('.vc-card__status');
    if (!panel || panel.dataset.busy === 'true') return;
    panel.dataset.busy = 'true';
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';
    try {
      const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component')).map(item => item.dataset.sectionId).filter(Boolean))];
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.vcVariant), quantity: 1 }], sections: sections.join(','), sections_url: window.location.pathname })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.errorMessage);
      const cartResponse = await fetch(this.dataset.cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.dataset.errorMessage);
      const cart = await cartResponse.json();
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'product',
        lines: [{ merchandiseId: button.dataset.vcVariant, quantity: 1 }], promise: deferred.promise
      }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'voce-card-size-picker', sourceId: this.closest('[id]')?.id || this.dataset.productId, itemCount: 1, sections: result.sections, didError: false }
      });
      const sheet = this.querySelector('.vc-size-sheet');
      if (sheet?.open) sheet.close();
      const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
      if (drawer) { await customElements.whenDefined('theme-drawer'); drawer.open(); }
    } catch (error) {
      if (status) status.textContent = error.message || this.dataset.errorMessage;
    } finally {
      panel.dataset.busy = 'false';
      button.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('voce-product-card')) customElements.define('voce-product-card', VoceProductCard);
